'use client';

import { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

interface ChartPoint {
  month: string;
  cumulative: number;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{value: number}>; label?: string }) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs text-[hsl(var(--foreground-muted))] mb-1">{label}</p>
      <p className={`font-mono font-bold text-lg tabular-nums ${val >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-500'}`}>
        {val >= 0 ? '+' : ''}{val.toFixed(2)} units
      </p>
    </div>
  );
}

export default function UnitsTrendChart() {
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const { data, error } = await supabase
          .from('picks')
          .select('posted_at, units_result')
          .neq('result', 'pending')
          .order('posted_at', { ascending: true });

        if (error) throw error;

        const picks = data || [];
        const byMonth: Record<string, number> = {};
        picks.forEach((p) => {
          const month = new Date(p.posted_at).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          byMonth[month] = (byMonth[month] || 0) + (p.units_result || 0);
        });

        let cumulative = 0;
        const points: ChartPoint[] = Object.entries(byMonth).map(([month, units]) => {
          cumulative += units;
          return { month, cumulative: parseFloat(cumulative.toFixed(2)) };
        });

        setChartData(points);
      } catch {
        setChartData([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const lastValue = chartData.length > 0 ? chartData[chartData.length - 1].cumulative : 0;

  if (loading) {
    return (
      <div className="stat-card mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Cumulative Units</h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))] mt-0.5">All-time unit performance trend</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-[220px] gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading chart...</span>
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="stat-card mb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Cumulative Units</h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))] mt-0.5">All-time unit performance trend</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-[220px]">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No graded picks yet. Chart will appear once picks are graded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stat-card mb-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Cumulative Units</h2>
          <p className="text-sm text-[hsl(var(--foreground-muted))] mt-0.5">All-time unit performance trend</p>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span className={`font-mono text-2xl font-bold tabular-nums ${lastValue >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
            {lastValue >= 0 ? '+' : ''}{lastValue.toFixed(1)}u
          </span>
          <span className="text-xs text-[hsl(var(--foreground-muted))]">total units</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
          <defs>
            <linearGradient id="unitsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(155, 65%, 38%)" stopOpacity={0.2} />
              <stop offset="95%" stopColor="hsl(155, 65%, 38%)" stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" vertical={false} />
          <XAxis dataKey="month" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}u`} />
          <ReferenceLine y={0} stroke="hsl(220, 13%, 82%)" strokeDasharray="4 4" />
          <Tooltip content={<CustomTooltip />} />
          <Area type="monotone" dataKey="cumulative" stroke="hsl(155, 65%, 38%)" strokeWidth={2.5} fill="url(#unitsGradient)" dot={false} activeDot={{ r: 5, fill: 'hsl(155, 65%, 38%)', stroke: 'white', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}